import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import './Hero.css';
import MovingBorderButton from '../ui/MovingBorderButton';
import { trackCTAClick } from '../../utils/analytics';
import { dispatchOpenWaitlist } from '../../utils/waitlistEvents';
     
interface HeroProps {
  onStart?: () => void;
  onWatchDemo?: () => void;
}                 

const Hero: React.FC<HeroProps> = ({ onStart, onWatchDemo: _onWatchDemo }) => {
  const sectionRef = useRef<HTMLElement>(null);

  const handleStart = () => {
    trackCTAClick('Get Started for free', '/signup');
    if (onStart) onStart();
    else dispatchOpenWaitlist('signup');
  };

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      if (reduced) {
        gsap.set('.hero-reveal-item', { opacity: 1, y: 0 });
        gsap.set('.hero-stone', { opacity: 1, y: 0 });
        return;
      }

      gsap.set('.hero-reveal-item', { opacity: 0, y: 40 });
      gsap.set('.hero-stone', { opacity: 0, y: 60 });

      const intro = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 0.05 });
      intro.to('.hero-reveal-item', { opacity: 1, y: 0, duration: 0.75, stagger: 0.1 }, 0.1);
      intro.to('.hero-stone', { opacity: 1, y: 0, duration: 1.1, stagger: 0.12, ease: 'power3.out' }, 0.3);
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="hero-section" data-analytics-section="hero">
      <div className="hero-bg" aria-hidden>
        <div className="hero-bg-gradient" />
        <img className="hero-stone hero-stone--left" src="/assets/img/leftstone-landingbg.png" alt="" aria-hidden />
        <img className="hero-stone hero-stone--right" src="/assets/img/rightstone-landingbg.png" alt="" aria-hidden />
      </div>

      <div className="hero-inner">
        <h1 className="hero-title">
          <span className="hero-reveal-item">Get referred by insiders</span>
          <span className="hero-reveal-item">at top companies</span>
        </h1>

        <p className="hero-subtitle hero-reveal-item">
          Request a referral from verified employees 
          <br />
          and land interviews cold applications never reach.
        </p>

        <div className="hero-cta-row hero-reveal-item">
          <MovingBorderButton
            className="hero-cta-primary"
            onClick={handleStart}
            duration={2500}
            glowColor="rgba(245, 158, 11, 0.8)"
            borderRadius="0.6rem"
          >
            Get Started
          </MovingBorderButton>
        </div>

        <div className="hero-media hero-reveal-item">
          <img
            className="hero-dashboard"
            src="/assets/img/landing-hero.jpg"
            alt="Reffconnect dashboard preview"
            loading="eager"
            decoding="async"
          />
        </div>
      </div>
    </section>
  );
};

export default React.memo(Hero);
